"use strict";
const yup = require('yup');
const validationOptions = require('./validationOptions');
/**
 * yup.addMethod modifies the yup module by mutating the referenced prototype, like so:
 * schemaType.prototype[name] = method
 * ie. object.prototype["oneOfSchemas"] = method
 *
 * see the docs for more info:
 * https://github.com/jquense/yup#yupaddmethodschematype-schema-name-string-method--schema-void
 */
// Important - don't use arrow functions in the addMethod params - as yup needs to bind `this`.
function oneOfSchemasValidateSingleItem(item, schemas) {
    // We create an array of either `true` or validation errors
    const results = schemas.map((schema) => {
        try {
            schema.validateSync(item, validationOptions);
            return true;
        }
        catch (error) {
            if (error.name === 'ValidationError')
                return error;
            throw error;
        }
    });
    // If at least one is true, then item matches a schema - so it is valid.
    if (results.some((v) => v === true)) {
        return true;
    }
    // We create a single validation error based on all the errors.
    const errorString = results.reduce((acc, cur) => `${acc},${JSON.stringify(cur.errors)}`, 'Item failed with the following groups of errors:');
    // We return (not _throw_) the error.
    const error = new yup.ValidationError(errorString);
    return error;
}
/**
 * Check that an object is one of the provided `schemas`
 */
yup.addMethod(yup.object, 'oneOfSchemas', function validate(schemas, allowNull = false) {
    if (!Array.isArray(schemas)) {
        throw new Error('oneOfSchemas expects an array of schemas');
    }
    return this.test({
        name: 'one-of-schemas',
        // eslint-disable-next-line no-template-curly-in-string
        message: '${path} did not match one of the required schemas',
        test(item) {
            if (item === null) {
                return allowNull;
            }
            const result = oneOfSchemasValidateSingleItem(item, schemas, this);
            if (result === true) {
                return true;
            }
            // this.createError preserve the path, params etc of the schema
            const error = this.createError();
            error.errors = result.errors;
            return error;
        },
    });
});
/**
 * Check that each object in an array is one of the provided `schemas`
 */
yup.addMethod(yup.array, 'oneOfSchemas', function validate(schemas, allowNull = false) {
    if (!Array.isArray(schemas)) {
        throw new Error('oneOfSchemas expects an array of schemas');
    }
    return this.test({
        name: 'one-of-schemas',
        // eslint-disable-next-line no-template-curly-in-string
        message: 'All items in ${path} did not match the required schemas',
        test(items = []) {
            // If allowNull, then a null array should return as valid.
            if (items === null) {
                return allowNull;
            }
            // For each item we get an array of either true, or the validation error.
            const results = items.map((item) => oneOfSchemasValidateSingleItem(item, schemas));
            // The array is only valid if all items are valid.
            if (results.every((v) => v === true)) {
                return true;
            }
            /**
             * Some notes for sorting this out later:
             *
             * - It seems that
             *
             * 1) Arrays work differently to objects in terms of validation - I suspect this is a bug.
             *    Note that for arrays `this.createError` doesn't work.
             *    Note that the array test doesn't seem to respect the error coming up.
             * 2) Similarly, refering to an array schema from parent object vs
             *    referring to an array schema directly also works differently.
             *
             *
             *  Really should raise an issue on Github for this.
             */
            const errorResults = results.filter((v) => v !== true);
            const error = this.createError('');
            error.inner = errorResults.reduce((acc, cur) => [...acc, ...cur.inner], []);
            error.errors = errorResults.reduce((acc, cur) => [...acc, ...cur.errors], []);
            return error;
        },
    });
});
module.exports = yup;
//# sourceMappingURL=modifiedYup.js.map